import { FulfillmentTypeKey } from '@carvana/merch-contracts/FulfillmentType';
import { useEffectWithController } from '@carvana/merch-react-hooks';
import { isSuccess } from '@carvana/merch-result';
import { useContext } from 'react';
import StaticContext, {
  useEventCorrelationId
} from '~/context/common/StaticContext';
import { useUserId, useUserLocation } from '~/context/common/UserContext';
import DeliveryPickupContext from '~/context/vdp/DeliveryPickupContext';
import { useGetVehicleDetails } from '~/context/vdp/VehicleContext';
import useCanShowDeliveryAndPickupInfo from '~/hooks/vdp/selectors/useCanShowDeliveryAndPickupInfo';
import fetchVehiclesDeliveryPickup from '~/services/common/fetchVehiclesDeliveryPickup';
import { generateErrorLogger } from '~/utilities/loggers';

export default function useGetDeliveryPickup() {
  const {
    deliveryPickup: { setData, setError, setLoading, setLoaded }
  } = useContext(DeliveryPickupContext);
  const { clientId } = useContext(StaticContext);
  const eventCorrelationId = useEventCorrelationId();
  const {
    fulfillmentType = FulfillmentTypeKey.Unknown,
    location: { locationId = 0 } = {},
    saleStatus = '',
    vehicleId = 0
  } = useGetVehicleDetails();
  const userId = useUserId();
  const { zip } = useUserLocation();
  const canShowDeliveryAndPickupInfo = useCanShowDeliveryAndPickupInfo();

  useEffectWithController(
    controller => {
      if (!canShowDeliveryAndPickupInfo || !zip) return;

      setLoading();

      fetchVehiclesDeliveryPickup(
        {
          clientId,
          correlationId: eventCorrelationId,
          userId,
          vehicles: [
            {
              vehicleInventoryType: fulfillmentType,
              vehicleLocationId: locationId,
              vehicleSaleStatus: saleStatus,
              vehicleId
            }
          ],
          zip
        },
        controller
      )
        .then(({ data }) => {
          if (!isSuccess(data)) {
            throw new Error(data.errorMessage);
          }

          if (data.content.length === 0) {
            throw new Error('No content found');
          }

          setData(data.content[0]);
        })
        .catch(
          generateErrorLogger(
            'Get delivery & pickup info',
            {},
            { callback: setError, controller }
          )
        );
    },
    [
      canShowDeliveryAndPickupInfo,
      clientId,
      eventCorrelationId,
      fulfillmentType,
      locationId,
      saleStatus,
      setData,
      setError,
      setLoaded,
      setLoading,
      userId,
      vehicleId,
      zip
    ]
  );
}
